import { DateTime } from "luxon";

export const DEFAULT_TIMEZONE = process.env.NEXT_PUBLIC_EVENT_TIMEZONE || "America/Argentina/Buenos_Aires";

export function nowInZone(timezone: string = DEFAULT_TIMEZONE): DateTime {
  return DateTime.now().setZone(timezone);
}

export function toUtcIso(value: DateTime): string {
  return value.toUTC().toISO() ?? "";
}

export function formatEventTime(utcIso: string, timezone: string = DEFAULT_TIMEZONE, locale = "es"): string {
  return DateTime.fromISO(utcIso, { zone: "utc" })
    .setZone(timezone)
    .setLocale(locale)
    .toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY);
}

/**
 * Capitalizes the first letter of a single word (e.g. weekday or month labels).
 */
function capitalizeWord(s: string): string {
  if (!s.length) return s;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/**
 * Formats the event date only in long form (e.g. "Sábado 14 de marzo") for the event timezone.
 * No time or local-time conversion is included.
 */
export function formatEventDateLong(
  utcIso: string,
  timezone: string = DEFAULT_TIMEZONE,
  locale = "es",
): string {
  return DateTime.fromISO(utcIso, { zone: "utc" })
    .setZone(timezone)
    .setLocale(locale)
    .toLocaleString({ weekday: "long", day: "numeric", month: "long" });
}

/**
 * Formats a compact event datetime for constrained surfaces such as the ticket QR
 * card (e.g. "Vie, 18 de Septiembre, 18:06hs"). Uses the event IANA timezone,
 * a three-letter weekday, a long capitalized month, 24-hour time, and an "hs" suffix.
 */
export function formatEventDateTimeCompact(
  utcIso: string,
  timezone: string = DEFAULT_TIMEZONE,
  locale = "es",
): string {
  const dt = DateTime.fromISO(utcIso, { zone: "utc" }).setZone(timezone).setLocale(locale);
  const weekday = capitalizeWord(dt.toFormat("ccc").replace(/\./g, "")).slice(0, 3);
  const day = dt.toFormat("d");
  const month = capitalizeWord(dt.toFormat("MMMM"));
  const time = dt.toFormat("HH:mm");
  const isEn = locale.toLowerCase().startsWith("en");
  const datePart = isEn
    ? `${weekday}, ${month} ${day}`
    : `${weekday}, ${day} de ${month}`;
  return `${datePart}, ${time}hs`;
}

/**
 * Formats the event date and time for consumer detail "Fecha y hora"
 * (e.g. "22 de Julio, 2026. 11:59hs"). Omits weekday so the value fits one line;
 * month is long and capitalized; year and time with "hs" suffix are always included.
 */
export function formatEventDateTimeLong(
  utcIso: string,
  timezone: string = DEFAULT_TIMEZONE,
  locale = "es",
): string {
  const dt = DateTime.fromISO(utcIso, { zone: "utc" }).setZone(timezone).setLocale(locale);
  const day = dt.toFormat("d");
  const month = capitalizeWord(dt.toFormat("MMMM"));
  const year = dt.toFormat("yyyy");
  const time = dt.toFormat("HH:mm");
  const isEn = locale.toLowerCase().startsWith("en");
  const datePart = isEn
    ? `${month} ${day}, ${year}`
    : `${day} de ${month}, ${year}`;
  return `${datePart}. ${time}hs`;
}

/**
 * Formats the event date for the compact meta row on the event detail page,
 * mirroring the mobile app ("Sáb 4 Oct 2026" / "Sat Oct 4 2026"): abbreviated
 * capitalized weekday and month, day and year, no time.
 */
export function formatEventDateShort(
  utcIso: string,
  timezone: string = DEFAULT_TIMEZONE,
  locale = "es",
): string {
  const dt = DateTime.fromISO(utcIso, { zone: "utc" }).setZone(timezone).setLocale(locale);
  const weekday = capitalizeWord(dt.toFormat("ccc").replace(/\./g, "")).slice(0, 3);
  const month = capitalizeWord(dt.toFormat("MMM").replace(/\./g, "")).slice(0, 3);
  const day = dt.toFormat("d");
  const year = dt.toFormat("yyyy");
  const isEn = locale.toLowerCase().startsWith("en");
  return isEn ? `${weekday} ${month} ${day} ${year}` : `${weekday} ${day} ${month} ${year}`;
}

/**
 * Formats the event start time as 24-hour "HH:mm" in the event timezone
 * (e.g. "23:15"), matching the mobile meta row.
 */
export function formatEventTime24(utcIso: string, timezone: string = DEFAULT_TIMEZONE): string {
  return DateTime.fromISO(utcIso, { zone: "utc" }).setZone(timezone).toFormat("HH:mm");
}

export function formatEventTimeWithLocal(
  utcIso: string,
  timezone: string = DEFAULT_TIMEZONE,
  locale = "es",
): { eventTime: string; localTime: string | null } {
  const zoned = DateTime.fromISO(utcIso, { zone: "utc" }).setZone(timezone).setLocale(locale);
  const local = zoned.setZone(DateTime.local().zoneName);

  // If offsets are the same, the time is effectively the same, so we don't need to show local time
  if (zoned.offset === local.offset) {
    return {
      eventTime: zoned.toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY),
      localTime: null,
    };
  }

  return {
    eventTime: zoned.toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY),
    localTime: local.toLocaleString(DateTime.DATETIME_MED),
  };
}

export function isValidUTCISO(iso: string): boolean {
  const dt = DateTime.fromISO(iso, { zone: "utc" });
  return dt.isValid && iso.endsWith("Z");
}

/**
 * Formats a date (or datetime) for swap cards with weekday and month abbreviated and capitalized
 * (e.g. "Dom, 26 Abr 2026" or "Dom, 26 Abr 2026, 21:00"). Uses locale for formatting then
 * capitalizes the first letter of the weekday and month segments.
 * Pass `includeYear: false` for compact strings like "Dom, 26 Abr, 21:00".
 */
export function formatSwapDateCapitalized(
  isoString: string,
  locale: string,
  options: { includeTime?: boolean; includeYear?: boolean } = {},
): string {
  const loc = locale.toLowerCase().startsWith("en") ? "en-US" : "es-AR";
  const dt = new Date(isoString);
  const dateStr = dt.toLocaleDateString(loc, {
    weekday: "short",
    ...(options.includeYear === false ? {} : { year: "numeric" as const }),
    month: "short",
    day: "numeric",
  });
  const timeStr = options.includeTime
    ? dt.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit", hour12: false })
    : null;
  const combined = timeStr ? `${dateStr}, ${timeStr}` : dateStr;
  const parts = combined.split(/\s+/);
  const out = parts
    .map((part) => {
      if (/^\d/.test(part) || /^\d,/.test(part)) return part;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(" ");
  return out;
}

